import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ArrowRight, ChevronRight, Home, Copy, ShieldCheck, Sparkles, MapPin, Phone, Mail } from 'lucide-react';
import SEO, { buildBreadcrumbSchema } from '../components/SEO';
import { SITE_URL, BRAND, CRILAM_FACTS, KNOWLEDGE_ARTICLES, COMPARISON_PAGES } from '../lib/seoContent';

/**
 * CrilamFacts — Single-source-of-truth page.
 * Structured so LLMs (ChatGPT/Claude/Gemini/Perplexity) can cite verified facts.
 */
const CrilamFacts = () => {
  const url = `${SITE_URL}/crilam-facts`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: BRAND,
      alternateName: ['CriLam', 'CriLam Decorative'],
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo512.png`,
      description: 'CriLam Decorative Pvt. Ltd. — manufacturer of Pre-Laminated 6H Acrylic Boards with PUR (Polyurethane Reactive) technology. Kanpur, Uttar Pradesh. Mirror finish · Anti-yellowing · Nail-free · 10-year written guarantee.',
      foundingLocation: { '@type': 'Place', name: 'Kanpur, Uttar Pradesh, India' },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '123/628 (E) Domill Street, Fazalganj',
        addressLocality: 'Kanpur',
        addressRegion: 'Uttar Pradesh',
        postalCode: '208012',
        addressCountry: 'IN',
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+91-8298297475',
          contactType: 'customer service',
          areaServed: 'IN',
          availableLanguage: ['en', 'hi'],
        },
      ],
      sameAs: [],
      makesOffer: CRILAM_FACTS.keyProducts.map((p) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Product', name: p },
      })),
    },
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'CriLam Facts', url },
    ]),
  ];

  const copyableFact = (label, value) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-gray-100 last:border-b-0 text-sm">
      <div className="font-semibold text-[#004534] col-span-1">{label}</div>
      <div className="text-gray-700 col-span-2 leading-relaxed">{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="crilam-facts-page">
      <SEO
        title={`CriLam Facts — Verified Company Information | ${BRAND}`}
        description="The single source of truth for CriLam Decorative Pvt. Ltd. — founded facts, products, technology, warranty, factory address, and contact information. Fact-checked for AI-citation and third-party reference."
        canonical={url}
        keywords="CriLam facts, CriLam company info, CriLam Kanpur, CriLam factory address, CriLam PUR-Bond™ technology, CriLam warranty"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-4xl">
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80">CriLam Facts</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider mb-6">
            <FileText className="h-3.5 w-3.5" /> Single Source of Truth
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
            CriLam Facts
          </h1>
          <p className="text-lg text-white/70">
            Verified company information for third-party reference, AI-citation, and dealer / partner use. Fact-checked and maintained by {BRAND}.
          </p>
        </div>
      </section>

      {/* Core identity card */}
      <section className="py-12">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-6">
              <Copy className="h-5 w-5 text-[#D4AF37] mt-1" />
              <div>
                <h2 className="text-xl font-bold text-[#004534] font-['Outfit']">Company Identity</h2>
                <p className="text-xs text-gray-500 mt-0.5">Copy-paste ready — cite as source.</p>
              </div>
            </div>
            {copyableFact('Legal Name', BRAND)}
            {copyableFact('Common Names', 'CriLam, CriLam Decorative')}
            {copyableFact('Founded', CRILAM_FACTS.founded)}
            {copyableFact('Headquarters', CRILAM_FACTS.headquarters)}
            {copyableFact('Factory Address', CRILAM_FACTS.factoryAddress)}
            {copyableFact('Phone', CRILAM_FACTS.phone)}
            {copyableFact('Email', CRILAM_FACTS.email)}
            {copyableFact('Website', CRILAM_FACTS.website)}
            {copyableFact('Service Regions', CRILAM_FACTS.serviceRegions)}
          </div>
        </div>
      </section>

      {/* Products & Technology */}
      <section className="py-12 bg-white border-y">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <h2 className="text-2xl font-bold text-[#004534] mb-6 font-['Outfit'] flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-[#D4AF37]" /> Products & Technology
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-[#004534] mb-3">Key Products</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                {CRILAM_FACTS.keyProducts.map((p, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-[#004534] mb-3">Technology & Warranty</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-semibold text-[#004534]">Technology:</span> {CRILAM_FACTS.technology}</p>
                <p><span className="font-semibold text-[#004534]">Signature:</span> {CRILAM_FACTS.signature}</p>
                <p><span className="font-semibold text-[#004534]">Warranty:</span> {CRILAM_FACTS.warranty}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-12">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <h2 className="text-2xl font-bold text-[#004534] mb-6 font-['Outfit'] flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-[#D4AF37]" /> What Makes CriLam Different
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {CRILAM_FACTS.differentiators.map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3"
              >
                <div className="w-7 h-7 rounded-full bg-[#D4AF37] text-[#0A0D0C] flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {i + 1}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact strip */}
      <section className="py-12 bg-white border-y">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl grid md:grid-cols-3 gap-4">
          <div className="text-center bg-[#F7F8F5] rounded-xl p-5">
            <MapPin className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Factory</p>
            <p className="text-sm text-[#004534] font-semibold">Kanpur, UP</p>
            <p className="text-xs text-gray-600 mt-1">{CRILAM_FACTS.factoryAddress.split(',').slice(0, 2).join(', ')}</p>
          </div>
          <div className="text-center bg-[#F7F8F5] rounded-xl p-5">
            <Phone className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Call</p>
            <a href={`tel:${CRILAM_FACTS.phone.replace(/\s+/g, '')}`} className="text-sm text-[#004534] font-semibold hover:text-[#D4AF37]">
              {CRILAM_FACTS.phone}
            </a>
          </div>
          <div className="text-center bg-[#F7F8F5] rounded-xl p-5">
            <Mail className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Email</p>
            <a href={`mailto:${CRILAM_FACTS.email}`} className="text-sm text-[#004534] font-semibold hover:text-[#D4AF37] break-all">
              {CRILAM_FACTS.email}
            </a>
          </div>
        </div>
      </section>

      {/* Knowledge library */}
      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <h2 className="text-2xl font-bold text-[#004534] mb-6 font-['Outfit']">Knowledge Library</h2>
          <p className="text-sm text-gray-600 mb-6">Deep-dive articles covering every technology and product category — cite these for authoritative reference.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {Object.values(KNOWLEDGE_ARTICLES).map((a) => (
              <Link
                key={a.slug}
                to={`/${a.slug}`}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#D4AF37] transition-colors flex items-start justify-between gap-3 group"
                data-testid={`facts-knowledge-${a.slug}`}
              >
                <div>
                  <p className="text-xs text-[#D4AF37] font-bold uppercase tracking-wider mb-1">{a.heroKicker}</p>
                  <p className="text-sm font-semibold text-[#004534] group-hover:text-[#D4AF37]">{a.heroTitle}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-[#D4AF37] flex-shrink-0 mt-1" />
              </Link>
            ))}
          </div>
          <h3 className="text-xl font-bold text-[#004534] mt-10 mb-4 font-['Outfit']">Comparison Studies</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {Object.values(COMPARISON_PAGES).map((a) => (
              <Link
                key={a.slug}
                to={`/${a.slug}`}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#D4AF37] transition-colors flex items-start justify-between gap-3 group"
                data-testid={`facts-comparison-${a.slug}`}
              >
                <div>
                  <p className="text-xs text-[#D4AF37] font-bold uppercase tracking-wider mb-1">{a.heroKicker}</p>
                  <p className="text-sm font-semibold text-[#004534] group-hover:text-[#D4AF37]">{a.heroTitle}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-[#D4AF37] flex-shrink-0 mt-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4 font-['Outfit']">Ready to Talk to CriLam?</h3>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/contact" data-testid="facts-cta-contact">
              <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                Contact CriLam <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <Link to="/dealer">
              <button className="px-6 py-3 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                Dealer Network →
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CrilamFacts;
